import { useState, useEffect } from 'react';
import {
    LayoutDashboard,
    Settings,
    Users,
    Activity,
    FileText,
    AlertTriangle,
    Thermometer,
    Zap,
    Droplets,
    Bell,
    Search,
    ChevronDown,
    ArrowUpRight,
    ShieldCheck,
    Power,
    Clock,
    Lightbulb,
    Download,
    Plus,
    Trash2,
    Edit,
    Cpu,
    Wifi,
    Calendar,
    Lock,
    ChevronRight,
    Eye
} from 'lucide-react';

export default function Dashboard() {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const formatDate = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        };
        return date.toLocaleString('es-CL', options) + ' GMT-03:00';
    };

    const menuItems = [
        { id: 'dashboard', icon: <LayoutDashboard size={20} />, label: 'Panel de Control' },
        { id: 'admin', icon: <Users size={20} />, label: 'Administración Usuarios' },
        { id: 'monitoring', icon: <Activity size={20} />, label: 'Monitoreo de Planta' },
        { id: 'efficiency', icon: <Zap size={20} />, label: 'Eficiencia Energética' },
        { id: 'reports', icon: <FileText size={20} />, label: 'Reportes de Generación' },
        { id: 'maintenance', icon: <Settings size={20} />, label: 'Mantenimiento Preventivo' },
        { id: 'security', icon: <ShieldCheck size={20} />, label: 'Seguridad y Accesos' },
        { id: 'iot', icon: <Lightbulb size={20} />, label: 'Dispositivos IoT' },
    ];

    // Render content based on activeTab
    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <div className="space-y-10">
                        {/* Row 1: Executive Summary & Indicators */}
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm relative overflow-hidden group/box">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full -mr-32 -mt-32 border border-slate-100 group-hover/box:scale-110 transition-transform duration-700" />

                            <div className="relative z-10 flex flex-col lg:flex-row gap-16">
                                <div className="flex-grow flex flex-col">
                                    <h3 className="text-xl font-black text-[#1E293B] tracking-tighter mb-12 flex items-center gap-4">
                                        <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                        Resumen Ejecutivo y Sostenibilidad
                                    </h3>

                                    <div className="grid md:grid-cols-3 gap-12">
                                        <div className="flex flex-col items-center">
                                            <div className="relative w-48 h-48 mb-6 group cursor-pointer">
                                                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                                    <circle cx="50" cy="50" r="42" stroke="#F1F5F9" strokeWidth="8" fill="none" />
                                                    <circle cx="50" cy="50" r="42" stroke="#6B9F3E" strokeWidth="12" fill="none" strokeDasharray="210 264" className="drop-shadow-[0_0_12px_rgba(107,159,62,0.3)]" />
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center -mb-2">
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ahorro</span>
                                                    <span className="text-[#6B9F3E] font-black text-2xl tracking-tighter">CLP 4.5M</span>
                                                    <span className="text-[10px] font-bold text-[#6B9F3E] mt-1">+25% vs Gas</span>
                                                </div>
                                            </div>
                                            <span className="text-[11px] font-black text-[#1E293B] uppercase tracking-[0.2em] text-center leading-relaxed">Ahorro Acumulado<br />(Mes)</span>
                                        </div>

                                        <div className="flex flex-col items-center">
                                            <div className="relative w-48 h-48 mb-6 cursor-pointer">
                                                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                                    <circle cx="50" cy="50" r="42" stroke="#F1F5F9" strokeWidth="8" fill="none" />
                                                    <circle cx="50" cy="50" r="42" stroke="#00A8E8" strokeWidth="12" fill="none" strokeDasharray="180 264" className="drop-shadow-[0_0_12px_rgba(0,168,232,0.3)]" />
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center -mb-2">
                                                    <Droplets className="text-[#00A8E8] mb-2" size={24} />
                                                    <span className="text-[#00A8E8] font-black text-4xl tracking-tighter">15.8</span>
                                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-1">TON CO₂</span>
                                                </div>
                                            </div>
                                            <span className="text-[11px] font-black text-[#1E293B] uppercase tracking-[0.2em] text-center leading-relaxed">Reducción de CO₂<br />(Toneladas)</span>
                                        </div>

                                        <div className="flex flex-col items-center">
                                            <div className="relative w-48 h-48 mb-6 group cursor-pointer">
                                                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                                    <circle cx="50" cy="50" r="42" stroke="#F1F5F9" strokeWidth="8" fill="none" />
                                                    <circle cx="50" cy="50" r="42" stroke="#F2994A" strokeWidth="12" fill="none" strokeDasharray="130 264" />
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center -mb-2">
                                                    <span className="text-[#F2994A] font-black text-3xl tracking-tighter">30m</span>
                                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">Costo Prod.</span>
                                                    <span className="text-[11px] font-bold text-slate-400">CLP 1.200.000</span>
                                                </div>
                                            </div>
                                            <span className="text-[11px] font-black text-[#1E293B] uppercase tracking-[0.2em] text-center leading-relaxed">Costo de Producción<br />(Mes)</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="xl:w-96 flex flex-col border-l border-slate-100 pl-16">
                                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.25em] mb-10 mt-2">Indicadores de Producción y Eficiencia</h4>
                                    <div className="space-y-12">
                                        <div>
                                            <div className="flex justify-between items-end mb-4">
                                                <span className="text-[11px] font-black text-slate-600 uppercase tracking-[0.15em]">Producción Acumulada</span>
                                                <span className="text-base font-black text-[#1E293B]">300.000 L</span>
                                            </div>
                                            <div className="h-4 bg-slate-100 rounded-full overflow-hidden p-1 shadow-inner">
                                                <div className="h-full bg-gradient-to-r from-blue-400 to-[#00A8E8] w-[85%] rounded-full shadow-[0_0_15px_rgba(0,168,232,0.4)]" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between items-end mb-4">
                                                <span className="text-[11px] font-black uppercase tracking-[0.15em]">Consumo Eléctrico Central</span>
                                                <span className="text-base font-black text-[#1E293B]">7.2M KWH</span>
                                            </div>
                                            <div className="h-4 bg-slate-100 rounded-full overflow-hidden p-1 shadow-inner">
                                                <div className="h-full bg-gradient-to-r from-emerald-400 to-[#6B9F3E] w-[62%] rounded-full shadow-[0_0_15px_rgba(107,159,62,0.4)]" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Row 2: System Status & Thermal Storage */}
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm flex flex-col lg:flex-row gap-16">
                            <div className="flex-grow flex flex-col">
                                <h3 className="text-xl font-black text-[#1E293B] tracking-tighter mb-10 flex items-center gap-4">
                                    <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                    Estado del Sistema (Planta Térmica)
                                </h3>
                                <div className="overflow-hidden border border-slate-100 rounded-[2rem] shadow-sm">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-100">
                                                <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Monitoreo</th>
                                                <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Estado Sist.</th>
                                                <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Alerta</th>
                                                <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Modo Op.</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {[
                                                { id: '01', state: 'Operativa', alert: 'Stand-by', mode: 'Calentando', color: 'green' },
                                                { id: '02', state: 'En espera', alert: 'En espera', mode: 'Stand-by', color: 'slate' },
                                                { id: '03', state: 'Operativa', alert: 'Falla Flujo', mode: 'Alerta', color: 'orange' },
                                            ].map((row) => (
                                                <tr key={row.id} className="hover:bg-slate-50/80 transition-all cursor-pointer">
                                                    <td className="px-8 py-6 font-bold text-[#1E293B] flex items-center gap-3">
                                                        <div className={`w-3 h-3 rounded-full ${row.color === 'green' ? 'bg-green-500' : 'bg-orange-500'}`} />
                                                        Bomba Calor {row.id}
                                                    </td>
                                                    <td className="px-8 py-6">
                                                        <span className="text-[10px] font-black uppercase tracking-widest px-4 py-2 bg-green-50 text-green-600 rounded-xl border border-green-100">{row.state}</span>
                                                    </td>
                                                    <td className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{row.alert}</td>
                                                    <td className="px-8 py-6 text-sm font-black text-slate-500">{row.mode}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="xl:w-96 flex flex-col border-l border-slate-100 pl-16">
                                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.25em] mb-12 mt-2">Almacenamiento Térmico (5.000 L)</h4>
                                <div className="flex-grow flex flex-col items-center justify-center p-8 bg-[#1E293B] rounded-[3rem] shadow-2xl relative overflow-hidden group/tanks">
                                    <div className="absolute inset-0 bg-mesh-gradient opacity-10" />
                                    <div className="flex gap-12 relative z-10 scale-110">
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-20 h-28 border-2 border-white/20 rounded-2xl relative overflow-hidden bg-white/5">
                                                <div className="absolute bottom-0 w-full h-[85%] bg-gradient-to-t from-blue-500 to-emerald-400 shadow-[0_0_20px_rgba(0,168,232,0.4)]" />
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="w-20 h-28 border-2 border-white/20 rounded-2xl relative overflow-hidden bg-white/5">
                                                <div className="absolute bottom-0 w-full h-[60%] bg-gradient-to-t from-blue-500 to-emerald-400 opacity-60" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-16 w-full space-y-4">
                                        <div className="flex justify-between items-end text-white">
                                            <span className="text-[11px] font-black tracking-widest">ESTANQUE A</span>
                                            <span className="text-lg font-black text-[#00A8E8]">82%</span>
                                        </div>
                                        <div className="h-3 bg-white/10 rounded-full overflow-hidden p-0.5">
                                            <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-400 w-[82%] rounded-full" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'admin':
                return (
                    <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm animate-fade-in">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className="text-xl font-black text-[#1E293B] tracking-tighter flex items-center gap-4">
                                <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                Administración de Usuarios y Roles
                            </h3>
                            <button className="bg-[#00A8E8] text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-3 hover:bg-[#007EA7] transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98]">
                                <Plus size={16} /> Agregar Usuario
                            </button>
                        </div>

                        <div className="overflow-hidden border border-slate-100 rounded-[2rem]">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100">
                                        <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Usuario</th>
                                        <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Rol</th>
                                        <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Estado</th>
                                        <th className="px-8 py-5 text-left text-[11px] font-black text-slate-400 uppercase tracking-widest">Último Acceso</th>
                                        <th className="px-8 py-5 text-center text-[11px] font-black text-slate-400 uppercase tracking-widest">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {[
                                        { name: 'Fabian Fernandez M.', email: 'f.fernandez@ciacgroup.cl', role: 'Administrador Gral.', status: 'Activo', last: 'Hoy, 10:42 AM', img: 'FF' },
                                        { name: 'Rodrigo Saavedra', email: 'r.saavedra@ciacgroup.cl', role: 'Supervisor Planta', status: 'Activo', last: 'Ayer, 18:20 PM', img: 'RS' },
                                        { name: 'Maria Ignacia Soto', email: 'm.soto@ciacgroup.cl', role: 'Analista ESG', status: 'Inactivo', last: '25 Ene, 2026', img: 'MS' },
                                        { name: 'Juan Carlos Perez', email: 'j.perez@ciacgroup.cl', role: 'Mantenimiento', status: 'Activo', last: 'Hoy, 09:15 AM', img: 'JP' },
                                    ].map((user, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/80 transition-all group">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-black text-xs text-slate-500 border border-slate-200">
                                                        {user.img}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-black text-[#1E293B]">{user.name}</span>
                                                        <span className="text-[10px] text-slate-400 font-bold">{user.email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <span className="bg-blue-50 text-[#00A8E8] text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border border-blue-100">{user.role}</span>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'Activo' ? 'bg-green-500' : 'bg-slate-300'}`} />
                                                    <span className={`text-[10px] font-black uppercase tracking-widest ${user.status === 'Activo' ? 'text-green-600' : 'text-slate-400'}`}>{user.status}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{user.last}</td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button className="p-2.5 text-slate-400 hover:text-[#00A8E8] hover:bg-blue-50 rounded-xl transition-all"><Edit size={16} /></button>
                                                    <button className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );

            case 'monitoring':
                return (
                    <div className="grid lg:grid-cols-2 gap-10 animate-fade-in">
                        <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm col-span-2">
                            <div className="flex justify-between items-center mb-10">
                                <h3 className="text-xl font-black text-[#1E293B] tracking-tighter flex items-center gap-4">
                                    <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                    Monitoreo de Planta - Telemetría Avanzada
                                </h3>
                                <div className="flex gap-4">
                                    <span className="flex items-center gap-2 text-[10px] font-black text-green-500 uppercase tracking-widest px-4 py-2 bg-green-50 rounded-xl border border-green-100">
                                        <Wifi size={14} /> Sistema Sincronizado
                                    </span>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-4 gap-8 mb-12">
                                {[
                                    { label: 'Temperatura Succión', val: '42.5°C', status: 'Estable', icon: <Thermometer /> },
                                    { label: 'Presión Alta', val: '18.2 bar', status: 'Normal', icon: <Activity /> },
                                    { label: 'Consumo Instantáneo', val: '12.4 kW', status: 'Bajo', icon: <Zap /> },
                                    { label: 'Caudal Bomba 02', val: '850 L/h', status: 'Óptimo', icon: <Droplets /> },
                                ].map((stat, i) => (
                                    <div key={i} className="bg-slate-50 border border-slate-100 rounded-3xl p-6 hover:shadow-lg transition-all">
                                        <div className="text-slate-400 mb-4">{stat.icon}</div>
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</div>
                                        <div className="text-2xl font-black text-[#1E293B] tracking-tighter">{stat.val}</div>
                                        <div className="text-[10px] font-bold text-green-600 mt-2 flex items-center gap-1">
                                            <div className="w-1 h-1 bg-green-500 rounded-full" /> {stat.status}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="h-64 w-full bg-slate-50 rounded-[2rem] border border-slate-100 flex items-center justify-center relative overflow-hidden">
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute top-1/2 left-0 w-full h-[1px] bg-blue-500" />
                                    <div className="absolute top-1/4 left-0 w-full h-[1px] bg-slate-400" />
                                    <div className="absolute top-3/4 left-0 w-full h-[1px] bg-slate-400" />
                                </div>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.3em]">Cargando Gráfico en Tiempo Real...</p>
                                {/* Simulated Graph Line */}
                                <svg className="absolute bottom-0 left-0 w-full h-32 text-[#00A8E8]">
                                    <path d="M0,100 Q100,20 200,80 T400,40 T600,100 T800,20 T1200,80" fill="none" stroke="currentColor" strokeWidth="3" className="animate-pulse" />
                                </svg>
                            </div>
                        </div>
                    </div>
                );

            case 'efficiency':
                return (
                    <div className="space-y-10 animate-fade-in">
                        <div className="grid md:grid-cols-2 gap-10">
                            <div className="bg-[#002838] text-white rounded-[3rem] p-10 shadow-2xl relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-48 h-48 bg-accent/20 rounded-full blur-3xl -mr-10 -mt-10" />
                                <h3 className="text-xl font-black tracking-tighter mb-8 flex items-center gap-4">
                                    <div className="w-2 h-8 bg-accent rounded-full" />
                                    Comparativa de Ahorro Energético
                                </h3>
                                <div className="space-y-8">
                                    <div className="flex items-end gap-6 h-48">
                                        <div className="flex-1 flex flex-col items-center gap-4 h-full justify-end">
                                            <div className="w-full bg-white/10 h-[90%] rounded-2xl relative">
                                                <div className="absolute top-2 w-full text-center text-[10px] font-black text-white/40">2024</div>
                                            </div>
                                            <span className="text-[10px] font-black uppercase text-white/50">Línea Base</span>
                                        </div>
                                        <div className="flex-1 flex flex-col items-center gap-4 h-full justify-end">
                                            <div className="w-full bg-accent h-[45%] rounded-2xl relative shadow-[0_0_30px_rgba(107,159,62,0.6)] animate-slide-up">
                                                <div className="absolute -top-10 w-full text-center text-sm font-black text-accent">-55%</div>
                                                <div className="absolute top-2 w-full text-center text-[10px] font-black text-white/60">2025</div>
                                            </div>
                                            <span className="text-[10px] font-black uppercase text-white/50 tracking-widest text-center px-2 leading-none">CIAC System Optimized</span>
                                        </div>
                                    </div>
                                    <div className="pt-8 border-t border-white/10">
                                        <p className="text-sm text-white/60 leading-relaxed italic">"La implementación de bombas de calor CIAC ha reducido el gasto operativo en combustibles fósiles en un 55.4% comparado con el período anterior."</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm">
                                <h3 className="text-xl font-black text-[#1E293B] tracking-tighter mb-10 flex items-center gap-4">
                                    <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                    Proyecciones de Sostenibilidad
                                </h3>
                                <div className="space-y-8">
                                    {[
                                        { label: 'Meta Reducción Huella Carbono', val: '85%', color: 'blue' },
                                        { label: 'Cumplimiento Normativa ESG', val: '100%', color: 'green' },
                                        { label: 'Eficiencia Térmica Lograda', val: '92.4%', color: 'orange' },
                                    ].map((p, i) => (
                                        <div key={i}>
                                            <div className="flex justify-between items-end mb-3">
                                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{p.label}</span>
                                                <span className="text-base font-black text-[#1E293B]">{p.val}</span>
                                            </div>
                                            <div className="h-3 bg-slate-100 rounded-full overflow-hidden p-0.5">
                                                <div className={`h-full rounded-full ${p.color === 'blue' ? 'bg-[#00A8E8]' : p.color === 'green' ? 'bg-[#6B9F3E]' : 'bg-orange-400'}`} style={{ width: p.val }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'reports':
                return (
                    <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm animate-fade-in">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className="text-xl font-black text-[#1E293B] tracking-tighter flex items-center gap-4">
                                <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                Repositorio de Reportes Operacionales
                            </h3>
                            <div className="flex gap-4">
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <input type="text" placeholder="Filtrar por fecha..." className="pl-12 pr-4 py-2.5 bg-slate-100 border-none rounded-xl text-xs outline-none w-64" />
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[
                                { title: 'Cierre Mensual Enero 2026', type: 'PDF', size: '4.2 MB', date: '30 Ene 2026', author: 'Fabian Fernandez' },
                                { title: 'Auditoría Eficiencia Térmica Q4', type: 'XLSX', size: '12.8 MB', date: '15 Ene 2026', author: 'Sistema IA' },
                                { title: 'Mantenimiento Preventivo BC-03', type: 'PDF', size: '2.4 MB', date: '05 Ene 2026', author: 'Rodrigo Saavedra' },
                                { title: 'Reporte ESG - Anual 2025', type: 'PDF', size: '25.0 MB', date: '31 Dic 2025', author: 'Admin General' },
                            ].map((report, idx) => (
                                <div key={idx} className="bg-slate-50 border border-slate-100 rounded-3xl p-6 hover:border-[#00A8E8] transition-all group overflow-hidden relative">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center text-[#00A8E8]">
                                            <FileText size={24} />
                                        </div>
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{report.type}</span>
                                    </div>
                                    <h4 className="text-sm font-black text-[#1E293B] mb-2">{report.title}</h4>
                                    <div className="flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-4">
                                        <span>{report.date}</span>
                                        <span>{report.size}</span>
                                    </div>
                                    <div className="mt-6 pt-6 border-t border-slate-200 flex justify-between items-center">
                                        <span className="text-[10px] font-bold text-slate-400">Por: {report.author}</span>
                                        <button className="p-2 text-[#00A8E8] hover:bg-[#00A8E8] hover:text-white rounded-lg transition-all"><Download size={18} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'maintenance':
                return (
                    <div className="grid lg:grid-cols-3 gap-10 animate-fade-in">
                        <div className="lg:col-span-2 bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm">
                            <h3 className="text-xl font-black text-[#1E293B] tracking-tighter mb-8 flex items-center gap-4">
                                <div className="w-2 h-8 bg-orange-400 rounded-full" />
                                Calendario de Mantenimiento
                            </h3>
                            <div className="grid grid-cols-7 gap-4 mb-2">
                                {['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'].map(d => (
                                    <div key={d} className="text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">{d}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-4">
                                {Array.from({ length: 31 }).map((_, i) => (
                                    <div key={i} className={`aspect-square rounded-2xl border flex flex-col p-2 transition-all relative group ${i + 1 === 30 ? 'bg-orange-50 border-orange-200 ring-2 ring-orange-500/20' : 'bg-white border-slate-100 hover:border-[#007EA7]'}`}>
                                        <span className={`text-xs font-black ${i + 1 === 30 ? 'text-orange-600' : 'text-slate-400'}`}>{i + 1}</span>
                                        {i + 1 === 30 && <div className="mt-auto w-full h-1 bg-orange-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.5)]" />}
                                        {i + 1 === 15 && <div className="mt-auto w-full h-1 bg-blue-500 rounded-full" />}

                                        {/* Tooltip on hover simulation */}
                                        {i + 1 === 30 && (
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-[#1E293B] text-white p-3 rounded-xl text-[10px] opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-2xl transition-opacity border border-slate-700">
                                                <div className="font-black uppercase tracking-widest mb-1">Mantenimiento BC-04</div>
                                                <div className="text-white/60">Inspección de compresores y limpieza de intercambiador.</div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-[3rem] p-10 border border-slate-200">
                            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.25em] mb-10 flex items-center gap-3">
                                <Calendar size={14} /> Próximas Tareas
                            </h4>
                            <div className="space-y-6">
                                {[
                                    { task: 'Limpieza Filtros Sector A', date: 'Mañana, 09:00', priority: 'Media' },
                                    { task: 'Calibración Sensores PT100', date: '05 Feb, 2026', priority: 'Baja' },
                                    { task: 'Cambio Válvula de Expansión', date: '12 Feb, 2026', priority: 'Crítica' },
                                ].map((t, i) => (
                                    <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:translate-x-2 transition-transform cursor-pointer">
                                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${t.priority === 'Crítica' ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-500'}`}>{t.priority}</span>
                                        <h5 className="text-sm font-black text-[#1E293B] mt-2 mb-1">{t.task}</h5>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.date}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'security':
                return (
                    <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm animate-fade-in relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-10 opacity-5">
                            <Lock size={200} />
                        </div>
                        <div className="relative z-10">
                            <div className="flex justify-between items-center mb-10">
                                <h3 className="text-xl font-black text-[#1E293B] tracking-tighter flex items-center gap-4">
                                    <div className="w-2 h-8 bg-blue-900 rounded-full" />
                                    Seguridad y Accesos de Plataforma
                                </h3>
                                <button className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-[#00A8E8] px-6 py-3 bg-blue-50 rounded-2xl border border-blue-100 hover:bg-blue-100 transition-all">
                                    <ShieldCheck size={18} /> Auditoría Completa
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-10">
                                <div className="space-y-6">
                                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-6">Logs de Acceso Recientes</h4>
                                    {[
                                        { user: 'Fabian Fernandez M.', action: 'Inicio Sesión', ip: '190.161.45.2', time: 'Hace 5 min' },
                                        { user: 'Rodrigo Saavedra', action: 'Modificó Parámetros PT', ip: '186.20.11.144', time: 'Hace 42 min' },
                                        { user: 'Desconocido', action: 'Intento de Acceso Fallido', ip: '45.10.8.2', time: 'Hace 2 horas', error: true },
                                    ].map((log, i) => (
                                        <div key={i} className={`p-5 rounded-2xl border flex items-center gap-6 transition-all ${log.error ? 'bg-red-50 border-red-100 ring-2 ring-red-500/10' : 'bg-slate-50 border-slate-100'}`}>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${log.error ? 'bg-red-500' : 'bg-[#1E293B]'}`}>
                                                <Lock size={16} />
                                            </div>
                                            <div className="flex-grow">
                                                <div className="flex justify-between">
                                                    <span className="text-sm font-black text-[#1E293B]">{log.user}</span>
                                                    <span className="text-[10px] font-bold text-slate-400">{log.time}</span>
                                                </div>
                                                <div className="flex justify-between items-center mt-1">
                                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{log.action}</span>
                                                    <span className="text-[10px] font-mono text-slate-400 bg-white px-2 py-0.5 rounded border border-slate-100">{log.ip}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-slate-900 text-white rounded-[2.5rem] p-10 flex flex-col justify-between group">
                                    <div>
                                        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-[#00A8E8] mb-8">
                                            <ShieldCheck size={32} />
                                        </div>
                                        <h5 className="text-2xl font-black tracking-tight mb-4">Cifrado de Datos AES-256 Activo</h5>
                                        <p className="text-white/40 text-sm leading-relaxed mb-10 italic">"Todas las comunicaciones entre sensores IoT de planta y este dashboard están protegidas por protocolos de seguridad de grado bancario."</p>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-white/40">
                                            <span>Nivel de Integridad</span>
                                            <span className="text-green-400">Excelente</span>
                                        </div>
                                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                            <div className="h-full bg-green-500 w-[98%] shadow-[0_0_15px_rgba(34,197,94,0.5)]" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'iot':
                return (
                    <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm animate-fade-in relative">
                        <div className="flex justify-between items-center mb-12">
                            <h3 className="text-xl font-black text-[#1E293B] tracking-tighter flex items-center gap-4">
                                <div className="w-2 h-8 bg-[#00A8E8] rounded-full" />
                                Red de Dispositivos y Sensores IoT
                            </h3>
                            <div className="flex gap-4">
                                <div className="flex items-center gap-4 bg-slate-50 px-6 py-3 rounded-2xl border border-slate-100">
                                    <div className="flex flex-col text-right">
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nodos Conectados</span>
                                        <span className="text-lg font-black text-[#1E293B]">42 / 42</span>
                                    </div>
                                    <div className="w-10 h-10 bg-green-50 text-green-500 rounded-xl flex items-center justify-center border border-green-100">
                                        <Wifi size={20} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                { name: 'Sensor PT-140', location: 'Estanque A', signal: 'Fuerta', battery: '92%', status: 'Online' },
                                { name: 'Gateway GW-02', location: 'Server Room', signal: 'Excelente', battery: 'AC Power', status: 'Online' },
                                { name: 'Actuador V-42', location: 'Bomba 01', signal: 'Baja', battery: '45%', status: 'Warning' },
                                { name: 'Cámara Térmica', location: 'Zona Motores', signal: 'Fuerta', battery: '78%', status: 'Online' },
                            ].map((device, i) => (
                                <div key={i} className="bg-slate-50 border border-slate-100 rounded-3xl p-8 group hover:border-[#00A8E8] hover:bg-white transition-all shadow-sm hover:shadow-xl relative overflow-hidden">
                                    <div className={`absolute top-0 right-0 w-2 h-full ${device.status === 'Online' ? 'bg-green-500' : 'bg-orange-400'} opacity-30`} />
                                    <div className="flex justify-between items-start mb-8">
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform group-hover:scale-110 ${device.status === 'Online' ? 'bg-white text-[#00A8E8]' : 'bg-orange-50 text-orange-500'}`}>
                                            <Cpu size={28} />
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded ${device.status === 'Online' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{device.status}</span>
                                            <span className="text-[10px] font-bold text-slate-400 mt-2 flex items-center gap-1"><Wifi size={10} /> {device.signal}</span>
                                        </div>
                                    </div>
                                    <h4 className="text-base font-black text-[#1E293B] mb-1">{device.name}</h4>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">{device.location}</p>

                                    <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 border-t border-slate-200 pt-6">
                                        <span className="flex items-center gap-2">Bat: {device.battery}</span>
                                        <button className="text-[#00A8E8] flex items-center gap-1 hover:underline">Config <ChevronRight size={12} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-12 bg-blue-50/50 border border-blue-100 rounded-[2rem] p-10 flex flex-col md:flex-row items-center gap-10">
                            <div className="w-24 h-24 bg-white rounded-3xl shadow-xl flex items-center justify-center text-[#00A8E8] flex-shrink-0 animate-bounce cursor-pointer hover:bg-blue-600 hover:text-white transition-all">
                                <Plus size={40} />
                            </div>
                            <div>
                                <h4 className="text-xl font-black text-[#1E293B] mb-2 tracking-tight underline decoration-[#00A8E8]/20 decoration-4">Sincronizar Nuevo Dispositivo IoT</h4>
                                <p className="text-slate-500 text-sm leading-relaxed max-w-xl">Haga clic en el botón para iniciar el escaneo de red Zigbee/LoRaWAN y emparejar nuevos sensores de telemetría o actuadores a su panel de control central.</p>
                            </div>
                        </div>
                    </div>
                );

            default:
                return <div>Seleccione una opción del menú</div>;
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex font-sans text-slate-800">
            {/* Sidebar */}
            <aside className={`${sidebarOpen ? 'w-72' : 'w-24'} bg-[#1E293B] transition-all duration-300 flex flex-col relative z-50 border-r border-slate-200`}>
                <div className="p-8 flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center p-2.5 shadow-xl flex-shrink-0">
                        <img src={`${import.meta.env.BASE_URL}images/logo.svg`} alt="CIAC" className="w-full" />
                    </div>
                    {sidebarOpen && (
                        <div className="flex flex-col min-w-0">
                            <span className="text-white font-black tracking-tight text-lg leading-none">CIAC ENERGY</span>
                            <span className="text-slate-400 text-[10px] uppercase tracking-widest mt-1">Sistemas Inteligentes</span>
                        </div>
                    )}
                </div>

                <nav className="flex-grow px-4 space-y-1.5 overflow-y-auto">
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full flex items-center gap-4 p-3.5 rounded-2xl transition-all group relative ${activeTab === item.id ? 'bg-[#00A8E8] text-white shadow-lg shadow-blue-500/20' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            <div className="flex-shrink-0">{item.icon}</div>
                            {sidebarOpen && <span className="font-bold text-sm tracking-wide truncate">{item.label}</span>}
                            {!sidebarOpen && (
                                <div className="absolute left-24 bg-[#1E293B] text-white px-3 py-1.5 rounded-lg text-xs opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-[100] border border-slate-700 shadow-2xl">
                                    {item.label}
                                </div>
                            )}
                        </button>
                    ))}
                </nav>

                <div className="p-6 mt-auto border-t border-slate-700/50">
                    <button className="w-full flex items-center gap-4 p-3.5 text-slate-400 hover:bg-red-500/10 hover:text-red-400 rounded-2xl transition-all group">
                        <Power size={20} className="group-hover:rotate-90 transition-transform" />
                        {sidebarOpen && <span className="font-bold text-sm tracking-wide">Cerrar Sesión</span>}
                    </button>
                </div>

                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="absolute -right-3 top-24 w-6 h-6 bg-[#00A8E8] rounded-full text-white flex items-center justify-center border-2 border-white shadow-xl z-50 hover:scale-110 transition-transform"
                >
                    {sidebarOpen ? <ChevronDown className="rotate-90" size={12} /> : <ChevronDown className="-rotate-90" size={12} />}
                </button>
            </aside>

            {/* Main Content */}
            <main className="flex-grow flex flex-col overflow-y-auto max-h-screen">
                {/* Top Header */}
                <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 px-10 py-5 flex items-center justify-between sticky top-0 z-40">
                    <div className="flex flex-col">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <Clock size={12} /> {formatDate(currentTime)}
                        </span>
                        <h2 className="text-2xl font-black text-[#1E293B] tracking-tight mt-1 underline decoration-[#00A8E8]/20 decoration-4 underline-offset-4">
                            {menuItems.find(m => m.id === activeTab)?.label} - CIAC Acuícola
                        </h2>
                    </div>

                    <div className="flex items-center gap-8">
                        <div className="relative group hidden xl:block">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input
                                type="text"
                                placeholder="Buscar activos o sensores..."
                                className="pl-12 pr-6 py-3 bg-slate-100 border-2 border-transparent rounded-[1.25rem] text-sm focus:bg-white focus:border-[#00A8E8]/20 focus:ring-4 focus:ring-[#00A8E8]/5 w-80 outline-none transition-all shadow-inner"
                            />
                        </div>

                        <div className="flex items-center gap-4 border-l border-slate-200 pl-8">
                            <div className="flex flex-col text-right">
                                <span className="text-sm font-black text-[#1E293B] tracking-tight">Fabian Fernandez Miralles</span>
                                <span className="text-[10px] font-bold text-[#00A8E8] uppercase tracking-widest">Administrador General</span>
                            </div>
                            <div className="w-12 h-12 rounded-[1.25rem] bg-gradient-to-br from-[#00A8E8] to-[#007EA7] text-white flex items-center justify-center font-black text-lg shadow-lg shadow-blue-500/20 ring-4 ring-white border border-blue-200 cursor-pointer overflow-hidden group/avatar">
                                <div className="absolute inset-0 bg-black/10 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center">
                                    <Eye size={16} />
                                </div>
                                FF
                            </div>
                        </div>
                    </div>
                </header>

                <div className="p-10 animate-fade-in max-w-7xl mx-auto w-full">
                    {renderContent()}
                </div>

                {/* Footer info */}
                <div className="px-10 py-8 mt-auto border-t border-slate-200 bg-white/50 flex flex-col md:flex-row justify-between items-center gap-6">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">© {new Date().getFullYear()} CIAC ENERGY SYSTEMS - POWERED BY READDY AI</p>
                    <div className="flex gap-4 md:gap-8 flex-wrap justify-center">
                        <span className="text-[10px] font-bold text-[#00A8E8] uppercase tracking-widest flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full border border-blue-100 italic"><ShieldCheck size={12} /> Nivel de Cifrado AES-256 Activo</span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-4 py-2 border border-slate-100 rounded-full">Sistemas Sincronizados v3.4.1</span>
                    </div>
                </div>
            </main>
        </div>
    );
}
